import { useGetPerformanceOffersReportQuery } from "api/TenantSlug/Report/hooks";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell";
import { last30DaysCreatedAtStr } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { PayoutResult, PerformanceReportDataSourceEnum, PerformanceReportViewEnum } from "types/enums";

import { COMPACT_DAYS } from "../shared/constants";
import { SearchableTable } from "../shared/SearchableTable";

type Series = App.Http.Responses.Report.PerformanceReportSeriesResponse;

export const BestOffers: React.FC = () => {
    const { isAdmin } = useUser();
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tR } = usePaldockTranslation("ui.report.columns");
    const userUtils = useUser();

    const q = useGetPerformanceOffersReportQuery(
        {
            page: {
                number: 1,
                size: COMPACT_DAYS,
            },
            filter: {
                filter: {
                    payout_result: {
                        in: [PayoutResult.Approved],
                    },
                    datetime_at: { btw: last30DaysCreatedAtStr },
                },
                sort: "-count",
                include: "offer",
            },
            view: isAdmin() ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
            source: PerformanceReportDataSourceEnum.ReportPayoutPerformances,
        },
        {
            staleTime: 5000,
        }
    );

    const rows = q.data?.series ?? [];

    return (
        <SearchableTable<Series>
            rows={rows}
            loading={q.isLoading}
            rowKey={(r) => `${r?.offer?.id ?? r?.offer?.title ?? "unknown"}-${r?.datetime_at ?? ""}`}
            searchKeys={[
                (r) => r?.offer?.title ?? r?.offer?.visible_title ?? "",
                (r) => String(r?.click ?? ""),
                (r) => String(r?.lead ?? ""),
                (r) => String(r?.count ?? ""),
                (r) => r?.revenue?.formatted_amount ?? String(r?.revenue?.amount ?? ""),
                (r) => r?.cost?.formatted_amount ?? String(r?.cost?.amount ?? ""),
                (r) => r?.profit?.formatted_amount ?? String(r?.profit?.amount ?? ""),
            ]}
            allowSearch={false}
            allowSuffix={false}
            columns={[
                {
                    title: tG("name"),
                    dataIndex: ["offer", "title"],
                    render: (_: unknown, r: Series) => <EntityLinkCell.Offer entity={r?.offer} offerTarget="auto" />,
                },
                { title: tR("clicks"), dataIndex: "click" },
                { title: tR("leads"), dataIndex: "lead" },
                { title: tR("transactions"), dataIndex: "count" },
                {
                    title: tR("revenue"),
                    dataIndex: "revenue",
                    render: (_: string, r: Series) => <MoneyCell money={r.revenue} />,
                },
                ...(userUtils.isAdmin()
                    ? [
                          {
                              title: tR("cost"),
                              dataIndex: "cost",
                              render: (_: string, r: Series) => <MoneyCell money={r.cost} />,
                          },
                      ]
                    : []),
                ...(userUtils.isAdmin()
                    ? [
                          {
                              title: tR("profit"),
                              dataIndex: "profit",
                              render: (_: string, r: Series) => <MoneyCell money={r.profit} />,
                          },
                      ]
                    : []),
            ]}
            maxRows={COMPACT_DAYS}
            compact
        />
    );
};
